import React, { useState, useMemo } from 'react';
import {
  Globe, Shield, Server, Database, Key, AlertTriangle, Target, User, Crown, Lock,
  Network, Wifi, Cloud, HardDrive, Monitor, Users, FileKey, Laptop, Router,
  ChevronDown, ChevronRight, Layers, Zap, Eye, Bug, Brain, Sparkles, Loader2,
  Copy, CheckCircle, RefreshCw, Code, Terminal, BookOpen
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Templates de chaînes d'attaque par type
const ATTACK_CHAIN_TEMPLATES = {
  web: {
    name: 'Web Application Attack',
    icon: Globe,
    color: 'cyan',
    description: 'Attaque via application web exposée',
    nodes: [
      { id: 'internet', label: 'Internet', sublabel: 'Attaquant externe', icon: Globe, color: 'cyan' },
      { id: 'waf', label: 'WAF / CDN', sublabel: 'Protection périmétrique', icon: Shield, color: 'orange' },
      { id: 'web-server', label: 'Serveur Web', sublabel: 'Apache/Nginx', icon: Server, color: 'blue' },
      { id: 'vuln-exploit', label: 'Exploitation', sublabel: 'SQLi / XSS / RCE', icon: Bug, color: 'red' },
      { id: 'app-server', label: 'Application', sublabel: 'Backend API', icon: Monitor, color: 'purple' },
      { id: 'database', label: 'Base de données', sublabel: 'PostgreSQL/MySQL', icon: Database, color: 'green' },
      { id: 'data-exfil', label: 'Exfiltration', sublabel: 'Données sensibles', icon: Lock, color: 'red' }
    ],
    connections: [
      { from: 'internet', to: 'waf' },
      { from: 'waf', to: 'web-server' },
      { from: 'web-server', to: 'vuln-exploit', type: 'attack' },
      { from: 'vuln-exploit', to: 'app-server', type: 'attack' },
      { from: 'app-server', to: 'database' },
      { from: 'database', to: 'data-exfil', type: 'attack' }
    ]
  },
  ad: {
    name: 'Active Directory Attack',
    icon: Users,
    color: 'purple',
    description: 'Compromission du domaine Active Directory',
    nodes: [
      { id: 'initial-access', label: 'Accès Initial', sublabel: 'Phishing / RCE', icon: AlertTriangle, color: 'red' },
      { id: 'workstation', label: 'Poste Client', sublabel: 'Windows 10/11', icon: Laptop, color: 'blue' },
      { id: 'recon', label: 'Reconnaissance', sublabel: 'BloodHound / LDAP', icon: Eye, color: 'yellow' },
      { id: 'priv-esc', label: 'Élévation Privilèges', sublabel: 'Kerberoasting / AS-REP', icon: Zap, color: 'orange' },
      { id: 'lateral', label: 'Mouvement Latéral', sublabel: 'Pass-the-Hash / Ticket', icon: Network, color: 'purple' },
      { id: 'dc', label: 'Domain Controller', sublabel: 'Windows Server', icon: Server, color: 'red' },
      { id: 'domain-admin', label: 'Domain Admin', sublabel: 'Contrôle Total', icon: Crown, color: 'yellow' }
    ],
    connections: [
      { from: 'initial-access', to: 'workstation', type: 'attack' },
      { from: 'workstation', to: 'recon' },
      { from: 'recon', to: 'priv-esc', type: 'attack' },
      { from: 'priv-esc', to: 'lateral', type: 'attack' },
      { from: 'lateral', to: 'dc', type: 'attack' },
      { from: 'dc', to: 'domain-admin', type: 'attack' }
    ]
  },
  network: {
    name: 'Network Infrastructure Attack',
    icon: Network,
    color: 'orange',
    description: 'Attaque infrastructure réseau',
    nodes: [
      { id: 'external', label: 'Réseau Externe', sublabel: 'Scan ports/services', icon: Globe, color: 'cyan' },
      { id: 'firewall', label: 'Firewall', sublabel: 'Règles ACL', icon: Shield, color: 'orange' },
      { id: 'dmz', label: 'Zone DMZ', sublabel: 'Services exposés', icon: Server, color: 'blue' },
      { id: 'vuln-service', label: 'Service Vulnérable', sublabel: 'SMB / RDP / SSH', icon: Bug, color: 'red' },
      { id: 'internal', label: 'Réseau Interne', sublabel: 'LAN corporatif', icon: Network, color: 'purple' },
      { id: 'router', label: 'Équipement Réseau', sublabel: 'Switch / Router', icon: Router, color: 'green' },
      { id: 'sensitive', label: 'Segments Critiques', sublabel: 'Serveurs / VLAN', icon: Lock, color: 'red' }
    ],
    connections: [
      { from: 'external', to: 'firewall' },
      { from: 'firewall', to: 'dmz' },
      { from: 'dmz', to: 'vuln-service', type: 'attack' },
      { from: 'vuln-service', to: 'internal', type: 'attack' },
      { from: 'internal', to: 'router' },
      { from: 'router', to: 'sensitive', type: 'attack' }
    ]
  },
  cloud: {
    name: 'Cloud Infrastructure Attack',
    icon: Cloud,
    color: 'blue',
    description: 'Compromission environnement cloud',
    nodes: [
      { id: 'public', label: 'Internet Public', sublabel: 'Reconnaissance', icon: Globe, color: 'cyan' },
      { id: 'cloud-entry', label: 'Point d\'entrée Cloud', sublabel: 'API / Console', icon: Cloud, color: 'blue' },
      { id: 'creds-leak', label: 'Fuite d\'Identifiants', sublabel: 'Clés API exposées', icon: Key, color: 'red' },
      { id: 'iam', label: 'IAM Compromise', sublabel: 'Rôle / Policy', icon: Users, color: 'purple' },
      { id: 'compute', label: 'Compute Resources', sublabel: 'EC2 / VM / Container', icon: Server, color: 'orange' },
      { id: 'storage', label: 'Storage', sublabel: 'S3 / Blob / GCS', icon: HardDrive, color: 'green' },
      { id: 'secrets', label: 'Secrets Manager', sublabel: 'Vault / KMS', icon: Lock, color: 'red' }
    ],
    connections: [
      { from: 'public', to: 'cloud-entry' },
      { from: 'cloud-entry', to: 'creds-leak', type: 'attack' },
      { from: 'creds-leak', to: 'iam', type: 'attack' },
      { from: 'iam', to: 'compute', type: 'attack' },
      { from: 'compute', to: 'storage' },
      { from: 'storage', to: 'secrets', type: 'attack' }
    ]
  },
  iot: {
    name: 'IoT / OT Attack',
    icon: Wifi,
    color: 'green',
    description: 'Attaque objets connectés / industriel',
    nodes: [
      { id: 'wireless', label: 'Accès Sans-fil', sublabel: 'WiFi / Zigbee / BLE', icon: Wifi, color: 'cyan' },
      { id: 'iot-device', label: 'Device IoT', sublabel: 'Capteur / Caméra', icon: Monitor, color: 'blue' },
      { id: 'firmware', label: 'Firmware Vuln', sublabel: 'Extraction / Analyse', icon: Bug, color: 'red' },
      { id: 'gateway', label: 'Gateway IoT', sublabel: 'Hub / Controller', icon: Router, color: 'purple' },
      { id: 'scada', label: 'SCADA / ICS', sublabel: 'Système industriel', icon: Layers, color: 'orange' },
      { id: 'plc', label: 'PLC / RTU', sublabel: 'Contrôleur', icon: HardDrive, color: 'green' },
      { id: 'process', label: 'Process Control', sublabel: 'Impact physique', icon: AlertTriangle, color: 'red' }
    ],
    connections: [
      { from: 'wireless', to: 'iot-device', type: 'attack' },
      { from: 'iot-device', to: 'firmware', type: 'attack' },
      { from: 'firmware', to: 'gateway', type: 'attack' },
      { from: 'gateway', to: 'scada' },
      { from: 'scada', to: 'plc' },
      { from: 'plc', to: 'process', type: 'attack' }
    ]
  }
};

// Fonction pour générer des chaînes d'attaque basées sur les vulnérabilités
const generateAttackChains = (vulnerabilities = [], targets = []) => {
  const chains = [];

  // Grouper les vulnérabilités par cible/hôte
  const vulnsByTarget = {};
  vulnerabilities.forEach(vuln => {
    const target = vuln.host_id || vuln.scan_target || vuln.target || 'unknown';
    if (!vulnsByTarget[target]) {
      vulnsByTarget[target] = [];
    }
    vulnsByTarget[target].push(vuln);
  });

  // Ajouter des chaînes basées sur les cibles
  targets.forEach(target => {
    const targetVulns = vulnsByTarget[target] || [];
    const chainType = detectChainType(target, targetVulns);

    if (chainType && ATTACK_CHAIN_TEMPLATES[chainType]) {
      chains.push({
        id: `chain-${target}`,
        target: target,
        type: chainType,
        template: ATTACK_CHAIN_TEMPLATES[chainType],
        vulnerabilities: targetVulns,
        riskScore: calculateRiskScore(targetVulns)
      });
    }
  });

  // Si pas de cibles spécifiques, générer des chaînes génériques
  if (chains.length === 0 && vulnerabilities.length > 0) {
    // Détecter les types de chaînes basées sur les vulnérabilités
    const hasWebVulns = vulnerabilities.some(v =>
      v.service?.toLowerCase().includes('http') ||
      v.port === 80 || v.port === 443 ||
      v.title?.toLowerCase().includes('xss') ||
      v.title?.toLowerCase().includes('sql')
    );

    const hasADVulns = vulnerabilities.some(v =>
      v.service?.toLowerCase().includes('ldap') ||
      v.service?.toLowerCase().includes('kerberos') ||
      v.port === 389 || v.port === 636 || v.port === 88
    );

    const hasNetworkVulns = vulnerabilities.some(v =>
      v.service?.toLowerCase().includes('smb') ||
      v.service?.toLowerCase().includes('ssh') ||
      v.port === 22 || v.port === 445 || v.port === 3389
    );

    if (hasWebVulns) {
      chains.push({
        id: 'chain-web-generic',
        target: 'Web Applications',
        type: 'web',
        template: ATTACK_CHAIN_TEMPLATES.web,
        vulnerabilities: vulnerabilities.filter(v => v.port === 80 || v.port === 443),
        riskScore: calculateRiskScore(vulnerabilities.filter(v => v.port === 80 || v.port === 443))
      });
    }

    if (hasADVulns) {
      chains.push({
        id: 'chain-ad-generic',
        target: 'Active Directory',
        type: 'ad',
        template: ATTACK_CHAIN_TEMPLATES.ad,
        vulnerabilities: vulnerabilities.filter(v => v.port === 389 || v.port === 88),
        riskScore: calculateRiskScore(vulnerabilities.filter(v => v.port === 389 || v.port === 88))
      });
    }

    if (hasNetworkVulns) {
      chains.push({
        id: 'chain-network-generic',
        target: 'Network Infrastructure',
        type: 'network',
        template: ATTACK_CHAIN_TEMPLATES.network,
        vulnerabilities: vulnerabilities.filter(v => v.port === 22 || v.port === 445),
        riskScore: calculateRiskScore(vulnerabilities.filter(v => v.port === 22 || v.port === 445))
      });
    }
  }

  // Toujours ajouter au moins une chaîne par défaut
  if (chains.length === 0) {
    chains.push({
      id: 'chain-default',
      target: 'Infrastructure Générale',
      type: 'network',
      template: ATTACK_CHAIN_TEMPLATES.network,
      vulnerabilities: vulnerabilities,
      riskScore: calculateRiskScore(vulnerabilities)
    });
  }

  return chains;
};

// Détecter le type de chaîne basé sur la cible
const detectChainType = (target, vulnerabilities) => {
  const targetLower = target.toLowerCase();

  if (targetLower.includes('ad') || targetLower.includes('dc') || targetLower.includes('ldap')) {
    return 'ad';
  }
  if (targetLower.includes('web') || targetLower.includes('http') || targetLower.includes('api')) {
    return 'web';
  }
  if (targetLower.includes('cloud') || targetLower.includes('aws') || targetLower.includes('azure') || targetLower.includes('gcp')) {
    return 'cloud';
  }
  if (targetLower.includes('iot') || targetLower.includes('scada') || targetLower.includes('plc')) {
    return 'iot';
  }

  // Détecter par vulnérabilités
  if (vulnerabilities.some(v => v.port === 389 || v.port === 88)) return 'ad';
  if (vulnerabilities.some(v => v.port === 80 || v.port === 443)) return 'web';

  return 'network';
};

// Calculer le score de risque
const calculateRiskScore = (vulnerabilities) => {
  if (!vulnerabilities || vulnerabilities.length === 0) return 0;

  let score = 0;
  vulnerabilities.forEach(v => {
    if (v.severity === 'critical') score += 40;
    else if (v.severity === 'high') score += 25;
    else if (v.severity === 'medium') score += 10;
    else if (v.severity === 'low') score += 5;

    if (v.cvss_score) score += v.cvss_score * 2;
  });

  return Math.min(100, score);
};

// Composant d'analyse LLM pour une chaîne d'attaque
const AIAttackAnalysis = ({ chain, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [activeTab, setActiveTab] = useState('methodology');

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  const generateAnalysis = async () => {
    setLoading(true);
    setError(null);

    try {
      // Construire le contexte pour l'analyse
      const vulnContext = chain.vulnerabilities.map(v => ({
        title: v.title || v.cve_id || 'Unknown',
        severity: v.severity || 'medium',
        cve: v.cve_id,
        port: v.port,
        service: v.service
      }));

      const prompt = `En tant qu'expert en sécurité offensive, analyse cette chaîne d'attaque de type "${chain.template.name}" ciblant "${chain.target}".

Type d'attaque: ${chain.type}
Description: ${chain.template.description}
Score de risque: ${chain.riskScore}%

Vulnérabilités identifiées:
${vulnContext.map(v => `- ${v.title} (${v.severity}${v.cve ? `, ${v.cve}` : ''}${v.port ? `, port ${v.port}` : ''})`).join('\n')}

Étapes de la chaîne d'attaque:
${chain.template.nodes.map((n, i) => `${i + 1}. ${n.label}: ${n.sublabel}`).join('\n')}

Fournis une analyse détaillée avec:
1. MÉTHODOLOGIE: Explique étape par étape comment un attaquant pourrait exploiter cette chaîne
2. OUTILS: Liste les outils spécifiques pour chaque étape avec des exemples de commandes
3. EXPLOITATION: Donne des exemples concrets de commandes ou payloads
4. INDICATEURS: Décris les IoC (Indicators of Compromise) à surveiller
5. REMÉDIATION: Propose des mesures de protection prioritaires

Réponds en français de manière technique et pratique.`;

      const response = await fetch(`${API_BASE}/api/v1/llm/infer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          prompt: prompt,
          max_tokens: 2000,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'analyse LLM');
      }

      const data = await response.json();

      // Parser la réponse en sections
      const responseText = data.response || data.text || '';
      const sections = {
        methodology: extractSection(responseText, 'MÉTHODOLOGIE', 'OUTILS'),
        tools: extractSection(responseText, 'OUTILS', 'EXPLOITATION'),
        exploitation: extractSection(responseText, 'EXPLOITATION', 'INDICATEURS'),
        indicators: extractSection(responseText, 'INDICATEURS', 'REMÉDIATION'),
        remediation: extractSection(responseText, 'REMÉDIATION', null),
        full: responseText
      };

      setAnalysis(sections);
    } catch (err) {
      console.error('AI Analysis error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const extractSection = (text, start, end) => {
    const startRegex = new RegExp(`${start}[:\\s]*`, 'i');
    const startMatch = text.match(startRegex);
    if (!startMatch) return '';

    const startIndex = text.indexOf(startMatch[0]) + startMatch[0].length;
    let endIndex = text.length;

    if (end) {
      const endRegex = new RegExp(`${end}[:\\s]*`, 'i');
      const endMatch = text.substring(startIndex).match(endRegex);
      if (endMatch) {
        endIndex = startIndex + text.substring(startIndex).indexOf(endMatch[0]);
      }
    }

    return text.substring(startIndex, endIndex).trim();
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const tabs = [
    { id: 'methodology', label: 'Méthodologie', icon: BookOpen },
    { id: 'tools', label: 'Outils', icon: Terminal },
    { id: 'exploitation', label: 'Exploitation', icon: Code },
    { id: 'indicators', label: 'IoC', icon: Eye },
    { id: 'remediation', label: 'Remédiation', icon: Shield }
  ];

  return (
    <div className="mt-4 bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-purple-900/30 border-b border-purple-500/20">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/20 border border-purple-500/30">
            <Brain className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h4 className="text-white font-bold flex items-center gap-2">
              Analyse LLM du Chemin d'Attaque
              <Sparkles className="w-4 h-4 text-yellow-400" />
            </h4>
            <p className="text-purple-300 text-sm">Explication détaillée par intelligence artificielle</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loading && (
            <button
              onClick={generateAnalysis}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
            >
              {analysis ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              {analysis ? 'Régénérer' : 'Générer l\'analyse'}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-purple-400 animate-spin mb-4" />
            <p className="text-purple-300">Analyse en cours par l'IA...</p>
            <p className="text-gray-500 text-sm mt-2">Génération de la méthodologie d'attaque</p>
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-400">{error}</p>
            <button
              onClick={generateAnalysis}
              className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm"
            >
              Réessayer
            </button>
          </div>
        )}

        {!loading && !error && !analysis && (
          <div className="text-center py-12">
            <Brain className="w-16 h-16 text-purple-400/50 mx-auto mb-4" />
            <h4 className="text-white font-semibold mb-2">Analyse IA disponible</h4>
            <p className="text-gray-400 text-sm mb-4 max-w-md mx-auto">
              Cliquez sur "Générer l'analyse" pour obtenir une explication détaillée de cette chaîne d'attaque,
              incluant la méthodologie, les outils, et les recommandations de sécurité.
            </p>
            <button
              onClick={generateAnalysis}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-lg font-medium transition-all transform hover:scale-105"
            >
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Générer l'analyse IA
              </span>
            </button>
          </div>
        )}

        {analysis && !loading && (
          <div>
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-700 pb-4">
              {tabs.map(tab => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <TabIcon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="relative">
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => copyToClipboard(analysis[activeTab] || analysis.full, activeTab)}
                  className="flex items-center gap-1 px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg text-sm transition-colors"
                >
                  {copied === activeTab ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-green-400">Copié!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copier
                    </>
                  )}
                </button>
              </div>

              <div className="bg-gray-900/50 rounded-lg p-4 pr-24 min-h-[200px] max-h-[400px] overflow-y-auto">
                <pre className="text-gray-300 text-sm whitespace-pre-wrap font-mono leading-relaxed">
                  {analysis[activeTab] || analysis.full || 'Aucune analyse disponible pour cette section.'}
                </pre>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-4 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>Analyse générée avec succès</span>
              </div>
              <button
                onClick={() => copyToClipboard(analysis.full, 'full')}
                className="ml-auto flex items-center gap-2 px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg text-sm transition-colors"
              >
                <Copy className="w-4 h-4" />
                Copier l'analyse complète
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour une seule chaîne d'attaque
const AttackChainVisualization = ({ chain, expanded, onToggle }) => {
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);
  const { template, target, vulnerabilities, riskScore } = chain;
  const IconComponent = template.icon;

  const getColorClasses = (color) => {
    const colors = {
      cyan: { bg: 'from-cyan-500/20 to-cyan-600/10', border: 'border-cyan-500/50', text: 'text-cyan-400' },
      orange: { bg: 'from-orange-500/20 to-orange-600/10', border: 'border-orange-500/50', text: 'text-orange-400' },
      blue: { bg: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-500/50', text: 'text-blue-400' },
      red: { bg: 'from-red-500/20 to-red-600/10', border: 'border-red-500/50', text: 'text-red-400' },
      purple: { bg: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-500/50', text: 'text-purple-400' },
      green: { bg: 'from-green-500/20 to-green-600/10', border: 'border-green-500/50', text: 'text-green-400' },
      yellow: { bg: 'from-yellow-500/20 to-yellow-600/10', border: 'border-yellow-500/50', text: 'text-yellow-400' }
    };
    return colors[color] || colors.blue;
  };

  const getRiskColor = (score) => {
    if (score >= 75) return 'text-red-400 bg-red-500/20';
    if (score >= 50) return 'text-orange-400 bg-orange-500/20';
    if (score >= 25) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-green-400 bg-green-500/20';
  };

  return (
    <div className="border border-gray-700 rounded-xl overflow-hidden mb-4">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-brand-black-light cursor-pointer hover:bg-gray-800/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${getColorClasses(template.color).bg} border ${getColorClasses(template.color).border}`}>
            <IconComponent className={`w-5 h-5 ${getColorClasses(template.color).text}`} />
          </div>
          <div>
            <h4 className="text-white font-bold">{target}</h4>
            <p className="text-gray-400 text-sm">{template.name} • {template.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(riskScore)}`}>
            Risque: {riskScore}%
          </div>
          <div className="text-gray-400 text-sm">
            {vulnerabilities.length} vuln{vulnerabilities.length > 1 ? 's' : ''}
          </div>
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 bg-brand-black border-t border-gray-700">
          {/* Attack Path Visualization */}
          <div className="relative overflow-x-auto pb-4">
            <div className="flex items-center gap-2 min-w-max">
              {template.nodes.map((node, index) => {
                const NodeIcon = node.icon;
                const colorClasses = getColorClasses(node.color);
                const connection = template.connections.find(c => c.from === node.id);
                const isAttack = connection?.type === 'attack';

                return (
                  <React.Fragment key={node.id}>
                    <div className={`flex-shrink-0 p-3 rounded-xl bg-gradient-to-br ${colorClasses.bg} border ${colorClasses.border} min-w-[140px]`}>
                      <div className="flex items-center gap-2 mb-1">
                        <NodeIcon className={`w-4 h-4 ${colorClasses.text}`} />
                        <span className="text-white text-sm font-medium">{node.label}</span>
                      </div>
                      <p className="text-gray-400 text-xs">{node.sublabel}</p>
                    </div>
                    {index < template.nodes.length - 1 && (
                      <div className="flex-shrink-0 flex items-center">
                        <div className={`w-8 h-0.5 ${isAttack ? 'bg-red-500' : 'bg-gray-600'}`}></div>
                        <div className={`w-0 h-0 border-t-4 border-b-4 border-l-8 border-transparent ${isAttack ? 'border-l-red-500' : 'border-l-gray-600'}`}></div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Vulnerabilities found in this chain */}
          {vulnerabilities.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <h5 className="text-sm font-medium text-gray-400 mb-2">Vulnérabilités associées:</h5>
              <div className="flex flex-wrap gap-2">
                {vulnerabilities.slice(0, 5).map((vuln, idx) => (
                  <span
                    key={idx}
                    className={`px-2 py-1 rounded text-xs ${
                      vuln.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                      vuln.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                      vuln.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}
                  >
                    {vuln.cve_id || vuln.title?.substring(0, 30) || 'Vulnerability'}
                  </span>
                ))}
                {vulnerabilities.length > 5 && (
                  <span className="px-2 py-1 rounded text-xs bg-gray-700 text-gray-400">
                    +{vulnerabilities.length - 5} autres
                  </span>
                )}
              </div>
            </div>
          )}

          {/* AI Analysis Toggle Button */}
          <div className="mt-4 pt-4 border-t border-gray-700">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAIAnalysis(!showAIAnalysis);
              }}
              className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                showAIAnalysis
                  ? 'bg-purple-600/20 border-purple-500/50 text-purple-300'
                  : 'bg-purple-900/20 border-purple-500/30 text-purple-400 hover:bg-purple-900/40 hover:border-purple-500/50'
              }`}
            >
              <Brain className="w-5 h-5" />
              <span className="font-medium">
                {showAIAnalysis ? 'Masquer l\'analyse IA' : 'Analyse LLM du Chemin d\'Attaque'}
              </span>
              <Sparkles className="w-4 h-4 text-yellow-400" />
              {showAIAnalysis ? (
                <ChevronDown className="w-4 h-4 ml-auto" />
              ) : (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </button>
          </div>

          {/* AI Analysis Panel */}
          {showAIAnalysis && (
            <AIAttackAnalysis chain={chain} onClose={() => setShowAIAnalysis(false)} />
          )}
        </div>
      )}
    </div>
  );
};

const AttackPathDiagram = ({ vulnerabilities = [], targets = [] }) => {
  const [expandedChains, setExpandedChains] = useState({});

  // Générer les chaînes d'attaque
  const attackChains = useMemo(() => {
    // Extraire les cibles uniques des vulnérabilités
    const uniqueTargets = [...new Set([
      ...targets,
      ...vulnerabilities.map(v => v.host_id || v.scan_target).filter(Boolean)
    ])];

    return generateAttackChains(vulnerabilities, uniqueTargets);
  }, [vulnerabilities, targets]);

  const toggleChain = (chainId) => {
    setExpandedChains(prev => ({
      ...prev,
      [chainId]: !prev[chainId]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    attackChains.forEach(chain => {
      allExpanded[chain.id] = true;
    });
    setExpandedChains(allExpanded);
  };

  const collapseAll = () => {
    setExpandedChains({});
  };

  return (
    <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Target className="w-6 h-6 text-brand-green" />
          <div>
            <h3 className="text-xl font-bold text-white">Chaînes d'Attaque Potentielles</h3>
            <p className="text-sm text-gray-400">
              {attackChains.length} scénario{attackChains.length > 1 ? 's' : ''} d'attaque identifié{attackChains.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Tout déplier
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Tout replier
          </button>
        </div>
      </div>

      {/* Attack Chains */}
      <div className="space-y-2">
        {attackChains.map(chain => (
          <AttackChainVisualization
            key={chain.id}
            chain={chain}
            expanded={expandedChains[chain.id] || false}
            onToggle={() => toggleChain(chain.id)}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="flex items-center justify-center gap-6 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-0.5 bg-gray-600"></div>
            <span className="text-gray-400">Flux normal</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-0.5 bg-red-500"></div>
            <span className="text-gray-400">Exploitation</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-cyan-400" />
            <span className="text-gray-400">Web</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-400" />
            <span className="text-gray-400">Active Directory</span>
          </div>
          <div className="flex items-center gap-2">
            <Network className="w-4 h-4 text-orange-400" />
            <span className="text-gray-400">Network</span>
          </div>
          <div className="flex items-center gap-2">
            <Cloud className="w-4 h-4 text-blue-400" />
            <span className="text-gray-400">Cloud</span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="mt-4 bg-brand-green/5 border border-brand-green/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🎯</div>
          <div className="text-sm text-gray-300">
            <p className="font-medium text-brand-green mb-1">Analyse Multi-Cibles</p>
            <p className="text-gray-400 text-xs">
              Chaque chaîne d'attaque représente un scénario d'exploitation potentiel basé sur les vulnérabilités
              découvertes et le type de cible (Web, Active Directory, Infrastructure réseau, Cloud, IoT/OT).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttackPathDiagram;
